/**
 * Carrier portal routes.
 *
 * Path-based, mirroring Hubble: the shell reads `window.location.pathname` and
 * dispatches to a view. Detail screens are real routes rather than overlays, so
 * they can be linked to from email, bookmarked, and reached with the back
 * button.
 *
 * Navigation always preserves the existing query string. It carries the signed
 * login token, and the auth flow re-reads that token on every load — dropping it
 * mid-session would silently downgrade a carrier from "authorized by link" to
 * "relying on a stored session".
 */

export type CarrierRoute =
  | { kind: "operationalIssues" }
  | { kind: "shipments" }
  | { kind: "shipment"; shipmentId: string }
  | { kind: "invoiceAudits" }
  | { kind: "performance" }
  | { kind: "customerSpecificPricing" }
  | { kind: "customerSpecificPricingAgreement"; agreementId: string }
  | { kind: "opportunity"; companyId: string }
  | { kind: "notFound" };

/** A top-level section in the nav bar. Detail routes map back to their section. */
export type CarrierSection =
  | "operationalIssues"
  | "shipments"
  | "invoiceAudits"
  | "performance"
  | "customerSpecificPricing";

export const CARRIER_SECTIONS: {
  id: CarrierSection;
  path: string;
  label: string;
}[] = [
  { id: "operationalIssues", path: "/", label: "Operational Issues" },
  { id: "shipments", path: "/shipments", label: "Shipments" },
  { id: "invoiceAudits", path: "/invoice-audits", label: "Invoice Audits" },
  { id: "performance", path: "/performance", label: "Performance" },
  {
    id: "customerSpecificPricing",
    path: "/customer-specific-pricing",
    label: "Customer Specific Pricing",
  },
];

/**
 * A link to one shipment, optionally opening a specific tab.
 *
 * `tab` is the query param ui-core `Tabs` reads on mount, so a link can point at
 * the Invoice tab directly — which is what the invoice audits index and the
 * audit banner's "See why" both need.
 */
export function shipmentHref(shipmentId: string, tab?: string): string {
  const path = `/shipments/${encodeURIComponent(shipmentId)}`;
  return tab === undefined ? path : `${path}?tab=${encodeURIComponent(tab)}`;
}

/**
 * One opportunity's own page. A real route rather than an overlay because the
 * opportunity email links straight to it, and because the full package is far
 * too much content for a dialog.
 */
export function opportunityHref(companyId: string): string {
  return `/opportunities/${encodeURIComponent(companyId)}`;
}

export function customerSpecificPricingAgreementHref(
  agreementId: string,
): string {
  return `/customer-specific-pricing/${encodeURIComponent(agreementId)}`;
}

// oxlint-disable-next-line sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function parseCarrierRoute(pathname: string): CarrierRoute {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  if (segments.length === 0) return { kind: "operationalIssues" };

  const [section, rawId] = segments;
  // The early return above means there is always a first segment, but the
  // index signature doesn't say so.
  if (section === undefined) return { kind: "notFound" };
  const id = rawId === undefined ? "" : decodeURIComponent(rawId);
  switch (section) {
    case "shipments":
      if (segments.length === 1) return { kind: "shipments" };
      if (segments.length === 2) return { kind: "shipment", shipmentId: id };
      return { kind: "notFound" };
    case "invoice-audits":
      return segments.length === 1
        ? { kind: "invoiceAudits" }
        : { kind: "notFound" };
    case "performance":
      return segments.length === 1
        ? { kind: "performance" }
        : { kind: "notFound" };
    case "opportunities":
      return segments.length === 2
        ? { kind: "opportunity", companyId: id }
        : { kind: "notFound" };
    case "customer-specific-pricing":
      if (segments.length === 1) return { kind: "customerSpecificPricing" };
      if (segments.length === 2)
        return { kind: "customerSpecificPricingAgreement", agreementId: id };
      return { kind: "notFound" };
    default:
      return { kind: "notFound" };
  }
}

/** Which nav item to highlight — a detail screen highlights its section. */
export function sectionForRoute(
  route: CarrierRoute,
): CarrierSection | undefined {
  switch (route.kind) {
    case "operationalIssues":
      return "operationalIssues";
    case "shipments":
    case "shipment":
      return "shipments";
    case "invoiceAudits":
      return "invoiceAudits";
    case "performance":
      return "performance";
    case "customerSpecificPricing":
    case "customerSpecificPricingAgreement":
    case "opportunity":
      return "customerSpecificPricing";
    case "notFound":
      return undefined;
  }
}

const LEGACY_VIEW_PATHS: Record<string, string> = {
  "operational-issues": "/",
  shipments: "/shipments",
  "invoice-audits": "/invoice-audits",
  performance: "/performance",
  csp: "/customer-specific-pricing",
};

/**
 * Rewrite a pre-routing login link to its path equivalent.
 *
 * Login links are permanent and bookmarked, and every one minted so far points
 * at `?view=<section>`, so they have to keep working.
 *
 * Only `view` is consumed. Every other param is carried across — `token` is the
 * carrier's credential, and `tab` selects the in-page tab (ui-core `Tabs` reads
 * it on mount), so a bookmark pointing at a specific Performance tab keeps
 * working.
 *
 * Runs before the app renders so there is no flash of the wrong section.
 */
export function migrateLegacyViewParam(): void {
  const params = new URLSearchParams(window.location.search);
  const view = params.get("view") ?? undefined;
  if (view === undefined) return;

  params.delete("view");
  const path = LEGACY_VIEW_PATHS[view] ?? "/";
  const search = params.toString();
  window.history.replaceState({}, "", search ? `${path}?${search}` : path);
}
